import {
  Button,
  Color,
  Flex,
  Icon,
  Modal,
  ModalProps,
  Spacing,
  Text,
  useModal,
  withModalConditionalRender,
} from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import { getInvoiceDocumentRouteBase } from '@features/invoices/utils/getInvoiceDocumentRouteBase'

import demoInvoiceCompletedIllustration from '../../../../assets/images/e-invoicing/e-invoice-approval-confirmation.svg'
import { useSegment } from '../../../../hooks'
import { useOrganizationViewUrl } from '../../../../hooks/routing/useOrganizationViewUrl'
import { reactRoute } from '../../../../utils/routing/reactRoute'
import * as Styled from './styles'

const ILLUSTRATION_SIZE = 249

const DemoInvoiceCompleted = (modalProps: ModalProps): ReactElement => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const { close } = useModal(modalProps.id)
  const { url: invoicesNewUrl } = useOrganizationViewUrl(
    reactRoute.organization.invoices.getNewDocumentRoute(getInvoiceDocumentRouteBase(InvoiceDocumentType.Invoice)),
  )
  const { url: eInvoicingUrl } = useOrganizationViewUrl(reactRoute.organization.eInvoicing.getMainRoute())

  const handleCreateInvoiceClick = useCallback(() => {
    track('XXX - Invoicing Core - Demo Invoice Success Modal - Create your first invoice clicked')
    close()
  }, [close, track])

  const handleActivateEInvoicingClick = useCallback(() => {
    track('XXX - Invoicing Core - Demo Invoice Success Modal - Activate e-invoicing clicked')
    close()
  }, [close, track])

  return (
    <Modal {...modalProps} closable size="s">
      <Modal.Header />
      <Modal.Body>
        <Styled.ContentWrapper>
          <Flex alignItems="center" flexDirection="column" gap={Spacing.S8}>
            <img alt="" height={ILLUSTRATION_SIZE} src={demoInvoiceCompletedIllustration} width={ILLUSTRATION_SIZE} />
            <Text variant="h3" weight="semiBold">
              {t('demo_invoice.completed_modal.title')}
            </Text>
            <Text colorVariant="secondary" variant="bodyLG">
              {t('demo_invoice.completed_modal.description')}
            </Text>
          </Flex>
          <Button onClick={handleCreateInvoiceClick} size="lg" to={invoicesNewUrl}>
            {t('empty_state.external_invoices.create_first_invoice')}
          </Button>
          <Styled.ActivationCard>
            <Styled.IconSquircle>
              <Icon color={Color.Black} icon="fileCheck" size="md" />
            </Styled.IconSquircle>
            <Styled.ActivationCardContent>
              <Text variant="h4" weight="semiBold">
                {t('e_invoice.kyc.step.get_started.title')}
              </Text>
              <Text colorVariant="secondary" variant="bodyMD">
                {t('demo_invoice.completed_modal.e_invoicing_description')}
              </Text>
            </Styled.ActivationCardContent>
            <Button onClick={handleActivateEInvoicingClick} to={eInvoicingUrl} variant="tonal">
              {t('einvoicing.settings.activation.activate_cta')}
            </Button>
          </Styled.ActivationCard>
        </Styled.ContentWrapper>
      </Modal.Body>
    </Modal>
  )
}

export const DemoInvoiceCompletedModal = withModalConditionalRender(DemoInvoiceCompleted)
